import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

const Section = ({ n, title, children }) => (
    <section className="mt-8">
        <h2 className="text-2xl font-serif font-medium">{n}. {title}</h2>
        <div className="text-muted-foreground leading-relaxed mt-3 space-y-3">{children}</div>
    </section>
);

export default function Privacy() {
    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="text-primary" size={30} />
                <h1 className="text-4xl sm:text-5xl font-serif font-medium">Informativa sulla Privacy</h1>
            </div>
            <p className="text-sm text-muted-foreground">Ultimo aggiornamento: {new Date().toLocaleDateString("it-IT")}</p>

            <p className="text-muted-foreground leading-relaxed mt-6">
                La presente informativa è resa ai sensi degli articoli 13 e 14 del Regolamento (UE) 2016/679
                (“GDPR”) e del D.lgs. 196/2003 (“Codice Privacy”) e descrive le modalità di trattamento dei dati
                personali degli utenti — sia scrittori sia editor — che utilizzano la piattaforma <strong>Incipit — Editoria Libera</strong>
                (di seguito “la Piattaforma” o “il Servizio”), un progetto gratuito e senza finalità di lucro.
            </p>

            <Section n="1" title="Titolare del trattamento">
                <p>
                    Il Titolare del trattamento è il gestore del progetto Incipit — Editoria Libera.
                    Per qualsiasi richiesta relativa ai dati personali è possibile scrivere a:{" "}
                    <a href="mailto:privacy@incipit.it" className="text-primary underline">privacy@incipit.it</a>.
                </p>
            </Section>

            <Section n="2" title="Dati personali trattati">
                <p>Trattiamo i dati che ci fornisci volontariamente e quelli generati dall’uso del Servizio:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li><strong>Dati di registrazione</strong>: nome, indirizzo email e password (conservata in forma cifrata). In caso di accesso con Google, nome, email e immagine del profilo forniti dal provider.</li>
                    <li><strong>Dati di profilo (editor)</strong>: biografia, località, generi letterari, case editrici, tariffe, testi curati, romanzi propri, immagini caricate e recapiti di contatto.</li>
                    <li><strong>Contenuti generati</strong>: recensioni, richieste di preventivo e relativi messaggi.</li>
                    <li><strong>Dati tecnici</strong>: dati di navigazione, indirizzo IP e cookie tecnici (vedi <Link to="/cookie" className="text-primary underline">Cookie Policy</Link>).</li>
                </ul>
            </Section>

            <Section n="3" title="Finalità e basi giuridiche del trattamento">
                <ul className="list-disc pl-6 space-y-1.5">
                    <li><strong>Erogazione del Servizio</strong> (creazione account, pubblicazione profili, ricerca editor, invio richieste di preventivo, recensioni) — base giuridica: esecuzione del contratto/servizio (art. 6.1.b GDPR).</li>
                    <li><strong>Pubblicazione del profilo editor</strong> e dei relativi contenuti, che sono per loro natura pubblicamente consultabili — base giuridica: consenso e legittimo interesse (art. 6.1.a e 6.1.f).</li>
                    <li><strong>Sicurezza, prevenzione abusi e adempimenti di legge</strong> — base giuridica: legittimo interesse e obbligo legale (art. 6.1.f e 6.1.c).</li>
                    <li><strong>Invio di comunicazioni funzionali</strong> (es. notifica all’editor di una richiesta di preventivo) — base giuridica: esecuzione del servizio.</li>
                </ul>
                <p>Il trattamento avviene previo <strong>consenso esplicito</strong> prestato in fase di registrazione, revocabile in qualsiasi momento.</p>
            </Section>

            <Section n="4" title="Modalità del trattamento">
                <p>
                    I dati sono trattati con strumenti informatici e telematici, adottando misure tecniche e organizzative
                    adeguate a garantirne sicurezza, integrità e riservatezza. Le password sono conservate esclusivamente
                    in forma cifrata (hashing) e non sono accessibili in chiaro.
                </p>
            </Section>

            <Section n="5" title="Comunicazione e destinatari">
                <p>I dati possono essere trattati da fornitori che agiscono come responsabili del trattamento, tra cui:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li>fornitori di hosting e infrastruttura cloud;</li>
                    <li>fornitore del servizio di invio email transazionali (per la notifica delle richieste di preventivo);</li>
                    <li>fornitore del servizio di autenticazione tramite Google (per l’accesso con Google).</li>
                </ul>
                <p>I dati non sono ceduti né venduti a terzi per finalità commerciali. I dati di profilo dell’editor sono per loro natura pubblici sulla Piattaforma.</p>
            </Section>

            <Section n="6" title="Conservazione dei dati">
                <p>
                    I dati sono conservati per il tempo necessario a erogare il Servizio e comunque fino alla richiesta
                    di cancellazione dell’account. L’Amministratore può rimuovere profili o account in caso di contenuti
                    non conformi, fraudolenti o lesivi. I log tecnici sono conservati per periodi limitati e proporzionati.
                </p>
            </Section>

            <Section n="7" title="Diritti dell’interessato">
                <p>Puoi esercitare in ogni momento i diritti previsti dagli artt. 15-22 del GDPR:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                    <li>accesso, rettifica, cancellazione (“diritto all’oblio”);</li>
                    <li>limitazione e opposizione al trattamento;</li>
                    <li>portabilità dei dati;</li>
                    <li>revoca del consenso in qualsiasi momento, senza pregiudicare la liceità del trattamento pregresso;</li>
                    <li>reclamo all’Autorità Garante per la protezione dei dati personali (www.garanteprivacy.it).</li>
                </ul>
                <p>Le richieste possono essere inviate a <a href="mailto:privacy@incipit.it" className="text-primary underline">privacy@incipit.it</a>.</p>
            </Section>

            <Section n="8" title="Minori">
                <p>Il Servizio non è destinato a minori di 16 anni. Non raccogliamo consapevolmente dati di minori senza il consenso di chi esercita la responsabilità genitoriale.</p>
            </Section>

            <Section n="9" title="Modifiche all’informativa">
                <p>La presente informativa può essere aggiornata nel tempo. Le modifiche saranno pubblicate su questa pagina con la data di aggiornamento.</p>
            </Section>

            <p className="text-xs text-muted-foreground mt-10 border-t border-border pt-6">
                Nota: il presente testo è un modello informativo di base fornito a scopo illustrativo. Ti consigliamo di
                farlo verificare da un professionista prima dell’uso in produzione, integrando i dati identificativi del Titolare.
            </p>
        </div>
    );
}
